import type { ChatUser } from '../../shared/events';
import { normalizeForSpeech } from '../../shared/speech-text';
import type { Settings } from './settings';

export type QueueKind = 'chat' | 'gift' | 'follow' | 'share' | 'join' | 'like';

export interface QueueItem {
  id: string;
  kind: QueueKind;
  user: ChatUser;
  /** Text shown on screen. */
  text: string;
  /** Text handed to the synthesizer. */
  speech: string;
  /**
   * BCP-47 language for voice selection, set only when the text's script forces one (Chinese,
   * Tamil, ...). null means "the current voice language", so switching language in the UI also
   * changes what is still waiting in the queue.
   */
  lang: string | null;
}

export type CloudStatus = 'off' | 'on' | 'exhausted';

export interface SpeechState {
  current: QueueItem | null;
  queue: QueueItem[];
  history: QueueItem[];
  paused: boolean;
  speaking: boolean;
  supported: boolean;
  /** Which engine is speaking the current item. */
  engine: 'cloud' | 'browser' | null;
  /** Premium (ElevenLabs) voice: off when not granted, exhausted when the budget ran out. */
  cloud: CloudStatus;
  cloudRemaining: number | null;
}

/** Languages the premium voice (ElevenLabs Flash v2.5) is asked to speak. */
const CLOUD_LANGS = new Set(['id', 'ms', 'en', 'zh', 'ta', 'ja', 'ko', 'ar']);
export const cloudSupports = (lang: string): boolean => CLOUD_LANGS.has(lang.toLowerCase().split('-')[0]);

type Listener = (s: SpeechState) => void;

const synth = (): SpeechSynthesis | null => (typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis : null);

export function listVoices(): SpeechSynthesisVoice[] {
  return synth()?.getVoices() ?? [];
}

export function onVoicesChanged(cb: () => void): () => void {
  const s = synth();
  if (!s) return () => {};
  s.addEventListener('voiceschanged', cb);
  return () => s.removeEventListener('voiceschanged', cb);
}

/** Finds the best voice for a language: exact tag, then language prefix, then any. */
export function pickVoice(lang: string, preferredURI: string | null, voices = listVoices()): SpeechSynthesisVoice | null {
  if (!voices.length) return null;
  const norm = (l: string) => l.toLowerCase().replace('_', '-');
  const want = norm(lang);
  const prefix = want.split('-')[0];
  if (preferredURI) {
    const v = voices.find((x) => x.voiceURI === preferredURI);
    if (v && norm(v.lang).startsWith(prefix)) return v;
  }
  const exact = voices.filter((v) => norm(v.lang) === want);
  const family = voices.filter((v) => norm(v.lang).startsWith(prefix + '-') || norm(v.lang) === prefix);
  const pool = exact.length ? exact : family;
  if (!pool.length) return null;
  // Natural (neural) voices first: Edge's "Online (Natural)" and Google's network voices sound far
  // better than the older local ones.
  const score = (v: SpeechSynthesisVoice) => (isNaturalVoice(v) ? 4 : 0) + (/google/i.test(v.name) ? 1 : 0) + (v.localService ? 0.5 : 0) + (v.default ? 0.25 : 0);
  return [...pool].sort((a, b) => score(b) - score(a))[0];
}

export function isNaturalVoice(v: SpeechSynthesisVoice): boolean {
  return /natural|neural/i.test(v.name);
}

/** True when this device has a natural-sounding voice for the language. */
export function hasNaturalVoice(lang: string, voices = listVoices()): boolean {
  const prefix = lang.toLowerCase().split('-')[0];
  return voices.some((v) => v.lang.toLowerCase().replace('_', '-').startsWith(prefix) && isNaturalVoice(v));
}

export const isEdge = (): boolean => typeof navigator !== 'undefined' && /\bEdg\//.test(navigator.userAgent);

/** "Microsoft Gadis Online (Natural) - Indonesian (Indonesia)" -> "Gadis". */
export function voiceShortName(v: SpeechSynthesisVoice | null): string {
  if (!v) return '';
  const name = v.name
    .replace(/^(Microsoft|Google|Apple)\s+/i, '')
    .replace(/\s+(Online|Desktop)\b.*$/i, '')
    .replace(/\s+-\s+.*$/, '')
    .replace(/\s*\(.*\)\s*$/, '')
    .trim();
  return name || v.name;
}

/** The language an item will actually be spoken in. */
export const itemLang = (item: Pick<QueueItem, 'lang'> | null | undefined, settings: Settings): string => item?.lang ?? settings.voiceLang;

const MAX_CHUNK = 180;

function chunk(text: string): string[] {
  if (text.length <= MAX_CHUNK) return [text];
  const parts: string[] = [];
  let rest = text;
  while (rest.length > MAX_CHUNK) {
    let cut = rest.lastIndexOf(' ', MAX_CHUNK);
    if (cut < 40) cut = MAX_CHUNK;
    parts.push(rest.slice(0, cut));
    rest = rest.slice(cut).trim();
  }
  if (rest) parts.push(rest);
  return parts;
}

/**
 * Bounded FIFO of things to say, driven by the Web Speech API. Handles the Chrome quirks
 * (utterances silently stopping after ~15 s, stuck "speaking" state) with a resume ticker
 * and a per-utterance watchdog.
 */
export class SpeechQueue {
  private state: SpeechState = {
    current: null,
    queue: [],
    history: [],
    paused: false,
    speaking: false,
    supported: Boolean(synth()) || typeof Audio !== 'undefined',
    engine: null,
    cloud: 'off',
    cloudRemaining: null,
  };
  private cloudToken: string | null = null;
  private audio: HTMLAudioElement | null = null;
  private audioUrl: string | null = null;
  private fetchAbort: AbortController | null = null;
  private listeners = new Set<Listener>();
  private settings: Settings;
  private ticker: number | null = null;
  private watchdog: number | null = null;
  private utteranceSeq = 0;

  constructor(settings: Settings) {
    this.settings = settings;
  }

  get snapshot(): SpeechState {
    return this.state;
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  setSettings(s: Settings): void {
    this.settings = s;
    if (!s.voiceOn) this.stopSpeaking();
    this.trim();
    this.pump();
  }

  /** Turns the premium voice on for this session (whitelisted streamers) or off. */
  setCloud(grant: { token: string; remaining: number } | null): void {
    this.cloudToken = grant?.token ?? null;
    if (!grant) this.set({ cloud: 'off', cloudRemaining: null });
    else this.set({ cloud: grant.remaining > 0 ? 'on' : 'exhausted', cloudRemaining: grant.remaining });
  }

  enqueue(item: QueueItem): void {
    if (!this.state.supported) return;
    this.set({ queue: [...this.state.queue, item] });
    this.trim();
    this.pump();
  }

  pause(): void {
    this.set({ paused: true });
    this.stopSpeaking();
  }

  resume(): void {
    this.set({ paused: false });
    this.pump();
  }

  skip(): void {
    this.stopSpeaking();
    this.pump();
  }

  readAgain(): void {
    const again = this.state.current ?? this.state.history[0];
    if (!again) return;
    this.stopSpeaking();
    this.set({ queue: [again, ...this.state.queue] });
    this.pump();
  }

  clear(): void {
    this.stopSpeaking();
    this.set({ queue: [], current: null });
  }

  /** Speaks a sample sentence immediately (used by the settings "Test voice" button). */
  test(sentence: string, lang: string | null): void {
    this.stopSpeaking();
    this.speakNow({ id: 'test', kind: 'chat', user: { uniqueId: '', nickname: '' }, text: sentence, speech: sentence, lang });
  }

  private set(patch: Partial<SpeechState>): void {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l(this.state));
  }

  private trim(): void {
    const max = Math.max(1, this.settings.queueMax);
    if (this.state.queue.length > max) this.set({ queue: this.state.queue.slice(this.state.queue.length - max) });
  }

  private pump(): void {
    if (this.state.speaking || this.state.paused || !this.settings.voiceOn) return;
    const [next, ...rest] = this.state.queue;
    if (!next) {
      if (this.state.current) this.set({ current: null });
      return;
    }
    this.set({ queue: rest });
    this.speakNow(next);
  }

  private speakNow(item: QueueItem): void {
    const seq = ++this.utteranceSeq;
    const lang = itemLang(item, this.settings);
    const text = normalizeForSpeech(item.speech, lang);
    // Busy LIVEs: read a little faster as the queue grows, up to 30% above the chosen speed.
    const boost = this.settings.speedUpWhenBusy ? Math.min(1.3, 1 + Math.max(0, this.state.queue.length - 2) * 0.05) : 1;
    const rate = Math.min(2, this.settings.rate * boost);
    const finish = () => {
      if (seq !== this.utteranceSeq) return;
      this.clearTimers();
      this.releaseAudio();
      const history = item.id === 'test' ? this.state.history : [item, ...this.state.history].slice(0, 20);
      this.set({ speaking: false, engine: null, history });
      this.pump();
    };
    if (!text) {
      // Nothing speakable left after cleanup (only emoji, links, ...): move on.
      this.set({ current: item, speaking: true, engine: null });
      window.setTimeout(finish, 0);
      return;
    }
    const useCloud = this.cloudToken && this.state.cloud === 'on' && this.settings.premiumVoice && cloudSupports(lang);
    if (useCloud) this.speakCloud(item, text, lang, rate, seq, finish);
    else this.speakBrowser(item, text, lang, rate, seq, finish);
  }

  /** ElevenLabs through the Worker; any failure falls back to the device voice for this item. */
  private speakCloud(item: QueueItem, text: string, lang: string, rate: number, seq: number, finish: () => void): void {
    this.set({ current: item, speaking: true, engine: 'cloud' });
    const abort = new AbortController();
    this.fetchAbort = abort;
    const fallback = () => {
      if (seq !== this.utteranceSeq) return;
      this.clearTimers();
      this.releaseAudio();
      this.speakBrowser(item, text, lang, rate, seq, finish);
    };
    // Covers slow networks and audio that never reports "ended".
    this.watchdog = window.setTimeout(() => {
      abort.abort();
      if (this.audio && !this.audio.ended && !this.audio.paused) return; // still playing a long clip
      fallback();
    }, 8000 + text.length * 120);

    fetch('/api/tts', {
      method: 'POST',
      headers: { authorization: `Bearer ${this.cloudToken}`, 'content-type': 'application/json' },
      body: JSON.stringify({ text, lang, speed: rate }),
      signal: abort.signal,
    })
      .then(async (res) => {
        if (res.status === 402) this.set({ cloud: 'exhausted', cloudRemaining: 0 });
        if (res.status === 401) this.setCloud(null);
        if (!res.ok) throw new Error(`tts ${res.status}`);
        const remaining = Number(res.headers.get('x-tts-remaining'));
        if (Number.isFinite(remaining)) this.set({ cloudRemaining: remaining });
        return res.blob();
      })
      .then((blob) => {
        if (seq !== this.utteranceSeq) return;
        this.audioUrl = URL.createObjectURL(blob);
        const audio = new Audio(this.audioUrl);
        audio.volume = this.settings.volume;
        this.audio = audio;
        audio.onended = finish;
        audio.onerror = fallback;
        return audio.play();
      })
      .catch(() => {
        // An abort comes from stopSpeaking (moved on) or the watchdog (which falls back itself).
        if (!abort.signal.aborted) fallback();
      });
  }

  private speakBrowser(item: QueueItem, text: string, lang: string, rate: number, seq: number, finish: () => void): void {
    const s = synth();
    this.set({ current: item, speaking: true, engine: 'browser' });
    if (!s) {
      window.setTimeout(finish, 0);
      return;
    }
    const voice = pickVoice(lang, this.settings.voiceURI);
    const parts = chunk(text);
    let done = 0;
    for (const part of parts) {
      const u = new SpeechSynthesisUtterance(part);
      if (voice) u.voice = voice;
      u.lang = voice?.lang ?? lang;
      u.rate = rate;
      u.pitch = this.settings.pitch;
      u.volume = this.settings.volume;
      u.onend = u.onerror = () => {
        done++;
        if (done >= parts.length) finish();
      };
      s.speak(u);
    }
    // Chrome desktop stops long utterances after ~15 s unless resumed periodically.
    this.ticker = window.setInterval(() => {
      if (s.speaking && !s.paused) s.resume();
    }, 10_000);
    const expectedMs = 2500 + (text.length * 90) / Math.max(0.5, rate);
    this.watchdog = window.setTimeout(() => {
      if (seq === this.utteranceSeq) {
        s.cancel();
        finish();
      }
    }, expectedMs + 8000);
  }

  private releaseAudio(): void {
    this.fetchAbort?.abort();
    this.fetchAbort = null;
    if (this.audio) {
      this.audio.onended = this.audio.onerror = null;
      this.audio.pause();
      this.audio = null;
    }
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
    this.audioUrl = null;
  }

  private stopSpeaking(): void {
    this.utteranceSeq++;
    this.clearTimers();
    this.releaseAudio();
    synth()?.cancel();
    if (this.state.speaking || this.state.current) {
      const history = this.state.current ? [this.state.current, ...this.state.history].slice(0, 20) : this.state.history;
      this.set({ speaking: false, engine: null, current: null, history });
    }
  }

  private clearTimers(): void {
    if (this.ticker) window.clearInterval(this.ticker);
    if (this.watchdog) window.clearTimeout(this.watchdog);
    this.ticker = null;
    this.watchdog = null;
  }
}
